import React, { useCallback, useEffect, useState } from "react";
import { isEqual } from "lodash";
import BaseField, { BaseFieldProps } from "./BaseField";
import { processSelectItems } from "../utils";
import { DropdownItemsType, useItemsDropdown } from "./Dropdown";
import { useDropdown } from "../hooks/use-dropdown";
import { PressableWithFocus } from "./PressableWithFocus";

type DropdownFieldProps<V> = Omit<BaseFieldProps, "component" | "hasValue" | "value"> & {
    items: (Omit<DropdownItemsType, "onClick"> & { value: V })[];
    onSelect: (value: V) => void;
    withScreenActionsPadding?: boolean;
    value?: V;
};

const DropdownField = <V,>({
    onSelect: propsOnSelect,
    items,
    asListAction,
    label,
    placeholder,
    value,
    style,
    error,
    left,
    right = "chevron-down",
    onBlur,
    numberOfLines,
    withScreenActionsPadding = true,
    ...props
}: DropdownFieldProps<V>) => {
    const [isFocused, setIsFocused] = useState(false);
    const onSelect = useCallback(
        (item: V) => {
            setIsFocused(false);
            propsOnSelect?.(item);
            onBlur?.();
        },
        [propsOnSelect, onBlur],
    );

    const [valueLabel, setValueLabel] = useState("");

    useEffect(() => {
        processSelectItems<V>(items, undefined, (items) => {
            const ret = items.find(
                (item) => isEqual(item.value, value) || isEqual(item, value),
            )?.label;
            if (ret) {
                setValueLabel(ret);
                return;
            } else if (typeof value === "string") {
                setValueLabel(value);
                return;
            }
            setValueLabel("");
        });
    }, [items, value]);

    const dropdownItems = items.map((e) => ({
        ...e,
        onClick: () => {
            onSelect(e.value);
        },
    }));

    const dropdownComponent = useItemsDropdown({
        items: dropdownItems,
    });

    const { onPress, ref } = useDropdown({
        component: dropdownComponent,
        withScreenActionsPadding,
        onBlur: () => {
            setIsFocused(false);
        },
    });

    return (
        <BaseField
            value={valueLabel}
            asListAction={asListAction}
            label={label}
            placeholder={placeholder}
            right={right}
            left={left}
            style={[style, { margin: 0 }]}
            error={error}
            disabled={props.disabled}
            numberOfLines={numberOfLines}
            isFocused={isFocused}
            asButton
            showErrorText={props.showErrorText}
            componentRef={ref}
            component={
                <PressableWithFocus
                    disabled={props.disabled}
                    onPress={() => {
                        setIsFocused(true);
                        onPress();
                    }}
                />
            }
        />
    );
};

export default DropdownField;
